/**
 * src/services/api.service.js
 * Central API service for HTTP requests with JWT token handling
 */

import axios from 'axios';
import authService from './auth.service';
import employeeAuthService from './employee-auth.service';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// Create an axios instance
const instance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Add a request interceptor
instance.interceptors.request.use(
  (config) => {
    // Önce employee tokenını kontrol edin
    let token = employeeAuthService.getAccessToken();
    
    // Eğer employee tokeni yoksa, admin tokenını kontrol edin
    if (!token) {
      token = authService.getAccessToken();
    }
    
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    
    // FormData kullanılıyorsa, Content-Type header'ı axios tarafından otomatik olarak ayarlanacaktır
    // Multipart/form-data istekleri için Content-Type'ı manuel olarak silmemiz gerekiyor
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor
instance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // If the error is 401 and hasn't been retried yet
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      // Check if employee is logged in
      if (employeeAuthService.isLoggedIn()) {
        // Try to refresh employee token
        const refreshed = await employeeAuthService.refreshToken();
        
        if (refreshed) {
          // If token refreshed successfully, retry the original request
          originalRequest.headers['Authorization'] = `Bearer ${employeeAuthService.getAccessToken()}`;
          return instance(originalRequest);
        } else {
          // If token refresh failed, redirect to employee login
          employeeAuthService.logout();
          window.location.href = '/employee/login';
          return Promise.reject(error);
        }
      } else {
        // If not employee, try to refresh admin token
        const refreshed = await authService.refreshToken();
        
        if (refreshed) {
          // If token refreshed successfully, retry the original request
          originalRequest.headers['Authorization'] = `Bearer ${authService.getAccessToken()}`;
          return instance(originalRequest);
        } else {
          // If token refresh failed, redirect to admin login
          authService.logout();
          window.location.href = '/admin/login';
          return Promise.reject(error);
        }
      }
    }
    
    return Promise.reject(error);
  }
);

// Helper for building query parameters
const buildQueryParams = (params) => {
  if (!params) return '';
  const queryParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, value);
    }
  });
  
  const queryString = queryParams.toString();
  return queryString ? `?${queryString}` : '';
};

// API methods
const apiService = {
  // Menu pages
  getMenuPages: () => instance.get('/api/menu-pages'),
  getActiveMenuPages: () => instance.get('/api/menu-pages/active'),
  getMenuPage: (id) => instance.get(`/api/menu-pages/${id}`),
  createMenuPage: (data) => instance.post('/api/menu-pages', data),
  updateMenuPage: (id, data) => instance.patch(`/api/menu-pages/${id}`, data),
  deleteMenuPage: (id) => instance.delete(`/api/menu-pages/${id}`),
  
  // Image uploads
  uploadMenuImage: (formData) => {
    return instance.post('/api/menu-pages/upload-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  
  // Gallery
  getGalleryImages: () => instance.get('/api/gallery'),
  getGalleryImagesByCategory: (category) => instance.get(`/api/gallery/category/${category}`),
  getGalleryImagesByDateRange: (startDate, endDate) => 
    instance.get(`/api/gallery/date-range?startDate=${startDate}&endDate=${endDate}`),
  uploadGalleryImage: (formData) => {
    return instance.post('/api/gallery/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  deleteGalleryImage: (id) => instance.delete(`/api/gallery/${id}`),
  
  // Todo (placeholder - implement based on your needs)
  getTodos: () => instance.get('/api/todo'),
  
  // Employee Management
  getEmployees: (filters) => instance.get(`/api/employees${buildQueryParams(filters)}`),
  getEmployee: (id) => instance.get(`/api/employees/${id}`),
  createEmployee: (data) => instance.post('/api/employees', data),
  updateEmployee: (id, data) => instance.put(`/api/employees/${id}`, data),
  deleteEmployee: (id) => instance.delete(`/api/employees/${id}`),
  getEmployeeShifts: (id, startDate, endDate) => {
    const params = { startDate, endDate };
    return instance.get(`/api/employees/${id}/shifts${buildQueryParams(params)}`);
  },
  
  // Employee Authentication
  setEmployeeCredentials: (id, credentials) => instance.post(`/api/employee-auth/${id}/set-password`, credentials),
  
  // Shift Types
  getShiftTypes: () => instance.get('/api/shift-types'),
  getShiftType: (id) => instance.get(`/api/shift-types/${id}`),
  createShiftType: (data) => instance.post('/api/shift-types', data),
  updateShiftType: (id, data) => instance.put(`/api/shift-types/${id}`, data),
  deleteShiftType: (id) => instance.delete(`/api/shift-types/${id}`),
  convertTimeFormat: (params) => instance.get(`/api/shift-types/utils/convert-time${buildQueryParams(params)}`),
  
  // Shifts
  getShifts: (filters) => instance.get(`/api/shifts${buildQueryParams(filters)}`),
  getWeeklyShifts: (weekStart) => instance.get(`/api/shifts/weekly?weekStart=${weekStart}`),
  getShift: (id) => instance.get(`/api/shifts/${id}`),
  createShift: (data) => instance.post('/api/shifts', data),
  updateShift: (id, data) => instance.put(`/api/shifts/${id}`, data),
  deleteShift: (id) => instance.delete(`/api/shifts/${id}`),
  bulkUpdateWeeklyShifts: (data) => instance.post('/api/shifts/weekly', data),
  
  // Employee specific methods
  getEmployeeWeeklyShifts: (employeeId, weekStart) => 
    instance.get(`/api/shifts/weekly?weekStart=${weekStart}&employeeId=${employeeId}`),
  getEmployeeMonthlyShifts: (employeeId, month) => 
    instance.get(`/api/shifts/monthly?month=${month}&employeeId=${employeeId}`),

    // Employee Availability methods
  createAvailability: (data) => instance.post('/api/availabilities', data),
  getEmployeeAvailabilities: (employeeId, startDate = null) => {
    const params = startDate ? { startDate } : {};
    return instance.get(`/api/availabilities/employee/${employeeId}${buildQueryParams(params)}`);
  },
  getWeeklyAvailabilities: (startDate) => instance.get(`/api/availabilities/week/${startDate}`),
  updateAvailabilityStatus: (id, data) => instance.put(`/api/availabilities/${id}/status`, data),
  deleteAvailability: (id) => instance.delete(`/api/availabilities/${id}`),


  // Tedarikçi Yönetimi
  getSuppliers: (filters) => instance.get(`/api/suppliers${buildQueryParams(filters)}`),
  getSupplier: (id) => instance.get(`/api/suppliers/${id}`),
  createSupplier: (data) => instance.post('/api/suppliers', data),
  updateSupplier: (id, data) => instance.put(`/api/suppliers/${id}`, data),
  deleteSupplier: (id) => instance.delete(`/api/suppliers/${id}`),
  getSupplierCategories: () => instance.get('/api/suppliers/categories'),
  deleteSupplierCategory: (id) => instance.delete(`/api/suppliers/categories/${id}`),

  // Ürün Yönetimi
  getProducts: (filters) => instance.get(`/api/products${buildQueryParams(filters)}`),
  getProduct: (id) => instance.get(`/api/products/${id}`),
  createProduct: (data) => instance.post('/api/products', data),
  updateProduct: (id, data) => instance.put(`/api/products/${id}`, data),
  deleteProduct: (id) => instance.delete(`/api/products/${id}`),
  updateProductStock: (id, data) => instance.patch(`/api/products/${id}/stock`, data),
  getStockAlerts: () => instance.get('/api/products/stock-alerts'),
  
  // Sipariş Yönetimi
  getOrders: (filters) => instance.get(`/api/orders${buildQueryParams(filters)}`),
  getOrder: (id) => instance.get(`/api/orders/${id}`),
  createOrder: (data) => instance.post('/api/orders', data),
  updateOrder: (id, data) => instance.put(`/api/orders/${id}`, data),
  deleteOrder: (id) => instance.delete(`/api/orders/${id}`),
  uploadOrderDocument: (orderId, formData) => {
    return instance.post(`/api/orders/${orderId}/documents`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  
  // Ödeme Yönetimi
  getPayments: (filters) => instance.get(`/api/payments${buildQueryParams(filters)}`),
  getPayment: (id) => instance.get(`/api/payments/${id}`),
  createPayment: (data) => instance.post('/api/payments', data),
  updatePayment: (id, data) => instance.put(`/api/payments/${id}`, data),
  deletePayment: (id) => instance.delete(`/api/payments/${id}`),
  uploadPaymentReceipt: (paymentId, formData) => {
    return instance.post(`/api/payments/${paymentId}/receipt`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  getSupplierTotalDebt: (supplierId) => instance.get(`/api/payments/supplier-debt/${supplierId}`),
  
  // Menü Kategori Yönetimi
  getMenuCategories: (filters) => instance.get(`/api/menu-categories${buildQueryParams(filters)}`),
  getMenuCategory: (id) => instance.get(`/api/menu-categories/${id}`),
  createMenuCategory: (data) => instance.post('/api/menu-categories', data),
  updateMenuCategory: (id, data) => instance.put(`/api/menu-categories/${id}`, data),
  deleteMenuCategory: (id) => instance.delete(`/api/menu-categories/${id}`),
  updateMenuCategoriesOrder: (data) => instance.patch('/api/menu-categories/update-order', data),
  
  // Menü Öğe Yönetimi
  getMenuItems: (filters) => instance.get(`/api/menu-items${buildQueryParams(filters)}`),
  getMenuItem: (id) => instance.get(`/api/menu-items/${id}`),
  createMenuItem: (data) => instance.post('/api/menu-items', data),
  updateMenuItem: (id, data) => instance.put(`/api/menu-items/${id}`, data),
  deleteMenuItem: (id) => instance.delete(`/api/menu-items/${id}`),
  uploadMenuItemImage: (id, formData) => {
    return instance.post(`/api/menu-items/${id}/image`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  updateMenuItemsOrder: (data) => instance.patch('/api/menu-items/update-order', data),
  
  // Reçete Yönetimi
  getRecipes: (filters) => instance.get(`/api/recipes${buildQueryParams(filters)}`),
  getRecipe: (id) => instance.get(`/api/recipes/${id}`),
  createOrUpdateRecipe: (data) => instance.post('/api/recipes', data),
  deleteRecipe: (id) => instance.delete(`/api/recipes/${id}`),
  
// Add these to the apiService object in api.service.js
  getEmployeeCheckInToday: () => instance.get('/api/employee/check-in/today'),
  submitEmployeeCheckIn: (locationData) => instance.post('/api/employee/check-in', locationData),
  getEmployeeCheckInHistory: () => instance.get('/api/employee/check-in/history'),
  getOfficeLocation: () => instance.get('/api/employee/office-location'),

  // Monthly check-in data
  getMonthlyCheckInData: (month, employeeId = null) => {
    const params = { month };
    if (employeeId) params.employeeId = employeeId;
    return instance.get(`/api/admin/check-ins/monthly${buildQueryParams(params)}`);
  },
  

  // Malzeme Yönetimi
  getIngredients: (filters) => instance.get(`/api/ingredients${buildQueryParams(filters)}`),
  getIngredient: (id) => instance.get(`/api/ingredients/${id}`),
  createIngredient: (data) => instance.post('/api/ingredients', data),
  updateIngredient: (id, data) => instance.put(`/api/ingredients/${id}`, data),
  deleteIngredient: (id) => instance.delete(`/api/ingredients/${id}`),
  uploadIngredientImage: (id, formData) => {
    return instance.post(`/api/ingredients/${id}/image`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },


// Employee Payment Management Methods (CORRECTED)
// Replace all existing employee payment methods with these:

  // Overview and Stats
  getMonthlyPaymentOverview: (month) => instance.get(`/api/employee-payments/overview/${month}`),
  getPaymentStats: (month) => instance.get(`/api/employee-payments/stats/${month}`),

  // Payment Rate Management
  getEmployeeRateHistory: (employeeId) => instance.get(`/api/employee-payments/rates/${employeeId}`),
  setEmployeeDailyRate: (employeeId, rateData) => instance.post(`/api/employee-payments/rates/${employeeId}`, rateData),

  // Payment Management
  createOrUpdatePayment: (paymentData) => instance.post('/api/employee-payments', paymentData),
  getEmployeePaymentHistory: (employeeId, month) => instance.get(`/api/employee-payments/history/${employeeId}/${month}`),
  deleteEmployeePayment: (id) => instance.delete(`/api/employee-payments/${id}`),

  // Backward compatibility methods (keep these for existing code)
  getEmployeePayments: (month) => instance.get(`/apiemployee-payments?month=${month}`),
  getEmployeePayment: (id) => instance.get(`/api/employee-payments/${id}`),
  createEmployeePayment: (paymentData) => instance.post('/api/employee-payments', paymentData),
  updateEmployeePayment: (id, paymentData) => instance.put(`/api/employee-payments/${id}`, paymentData),

  getEmployeeShiftSummary: (month) => instance.get(`/api/employee-payments/shifts/summary?month=${month}`),
  addEmployeePayment: (paymentData) => instance.post('/api/employee-payments', paymentData),
  getEmployeePaymentHistory: (employeeId, month) => instance.get(`/api/employee-payments/history/${employeeId}/${month}`),
  deleteIndividualPayment: (paymentId) => instance.delete(`/api/employee-payments/${paymentId}`),

  // ============================================
  // SALES REPORTS MANAGEMENT
  // ============================================
  
  // Dashboard Summary Data (Public - no auth required)
  getSalesTodaySummary: () => instance.get('/api/sales/today-summary'),
  getSalesWeeklyTrend: () => instance.get('/api/sales/weekly-trend'),
  getSalesTopProducts: (filters) => instance.get(`/api/sales/top-products${buildQueryParams(filters)}`),
  getSalesRecentReports: (limit = 10) => instance.get(`/api/sales/recent-reports?limit=${limit}`),

  // Admin Sales Reports Management (Auth required)
  getSalesReports: (filters) => instance.get(`/api/sales/reports${buildQueryParams(filters)}`),
  getSalesReport: (id) => instance.get(`/api/sales/reports/${id}`),
  getSalesDailyAggregations: (filters) => instance.get(`/api/sales/daily-aggregations${buildQueryParams(filters)}`),
  getSalesAnalytics: (period = 'week') => instance.get(`/api/sales/analytics?period=${period}`),

  // Helper methods for specific use cases
  getSalesTopProductsToday: () => instance.get('/api/sales/top-products?period=today&limit=5'),
  getSalesTopProductsWeek: () => instance.get('/api/sales/top-products?period=week&limit=10'),
  getSalesTopProductsMonth: () => instance.get('/api/sales/top-products?period=month&limit=10'),
  
  getSalesReportsToday: () => {
    const today = new Date().toISOString().split('T')[0];
    return instance.get(`/api/sales/reports?startDate=${today}&endDate=${today}`);
  },
  
  getSalesReportsWeek: () => {
    const today = new Date();
    const weekStart = new Date(today.setDate(today.getDate() - 6)).toISOString().split('T')[0];
    const weekEnd = new Date().toISOString().split('T')[0];
    return instance.get(`/api/sales/reports?startDate=${weekStart}&endDate=${weekEnd}`);
  },

  getSalesReportsMonth: () => {
    const today = new Date();
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
    const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];
    return instance.get(`/api/sales/reports?startDate=${monthStart}&endDate=${monthEnd}`);
  },

  // Sales analytics with different periods
  getSalesAnalyticsToday: () => instance.get('/api/sales/analytics?period=today'),
  getSalesAnalyticsWeek: () => instance.get('/api/sales/analytics?period=week'),
  getSalesAnalyticsMonth: () => instance.get('/api/sales/analytics?period=month'),

  // Generic CRUD operations
  get: (url) => instance.get(url),
  post: (url, data, config) => instance.post(url, data, config),
  put: (url, data, config) => instance.put(url, data, config),
  patch: (url, data, config) => instance.patch(url, data, config),
  delete: (url) => instance.delete(url)
};

export default apiService;